import { IServerOrder } from '@rbi-ctg/menu';
import { ServiceMode } from 'generated/graphql-gateway';

type DeliveryAddressForReprice = {
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

const normalizeAddressPart = (value?: string | null) => value?.trim().toLowerCase() ?? '';

/** Zip+4 and bare 5-digit should compare equal for settlement. */
const normalizeZip = (value?: string | null) => normalizeAddressPart(value).split('-')[0];

const secondaryAddressPartsMatch = (
  selected?: string | null,
  server?: string | null,
  normalize: (value?: string | null) => string = normalizeAddressPart
) => {
  const selectedPart = normalize(selected);
  const serverPart = normalize(server);
  // Missing secondary fields on either side are treated as wildcards so partial
  // server dropoffs cannot trap checkout in endless reprice loading.
  if (!selectedPart || !serverPart) {
    return true;
  }
  return selectedPart === serverPart;
};

export const deliveryAddressesMatch = (
  selectedAddress?: DeliveryAddressForReprice | null,
  serverDropoff?: DeliveryAddressForReprice | null
) => {
  if (!selectedAddress || !serverDropoff) {
    return false;
  }

  if (
    normalizeAddressPart(selectedAddress.addressLine1) !==
    normalizeAddressPart(serverDropoff.addressLine1)
  ) {
    return false;
  }

  return (
    secondaryAddressPartsMatch(selectedAddress.city, serverDropoff.city) &&
    secondaryAddressPartsMatch(selectedAddress.state, serverDropoff.state) &&
    secondaryAddressPartsMatch(selectedAddress.zip, serverDropoff.zip, normalizeZip)
  );
};

export const isDeliveryQuoteOrAddressStale = ({
  quoteId,
  serverOrderDeliveryQuoteId,
  deliveryAddress,
  serverOrderDropoff,
}: {
  quoteId?: string | null;
  serverOrderDeliveryQuoteId?: string | null;
  deliveryAddress?: DeliveryAddressForReprice | null;
  serverOrderDropoff?: DeliveryAddressForReprice | null;
}) => {
  // Prefer dropoff match over quoteId: after an address change, local quoteId often
  // lags the priced server quote even when delivery is already settled. Treating that
  // as pending is what kept Checkout V2 in endless loading (BKPE-27803 follow-up).
  if (deliveryAddressesMatch(deliveryAddress, serverOrderDropoff)) {
    return false;
  }

  if (quoteId && serverOrderDeliveryQuoteId) {
    return quoteId !== serverOrderDeliveryQuoteId;
  }

  return true;
};

export const getServerOrderStoreNumber = (serverOrder?: IServerOrder | null) =>
  serverOrder?.cart?.storeDetails?.storeNumber ?? serverOrder?.cart?.storeId?.toString?.() ?? null;

export const isCheckoutStoreRepriceStale = ({
  selectedStoreNumber,
  serverOrder,
}: {
  selectedStoreNumber?: string | null;
  serverOrder?: IServerOrder | null;
}) => {
  const serverOrderStoreNumber = getServerOrderStoreNumber(serverOrder);

  return Boolean(
    selectedStoreNumber && serverOrderStoreNumber && selectedStoreNumber !== serverOrderStoreNumber
  );
};

export const shouldMarkCheckoutRepriceRequestCompleted = ({
  checkoutRepriceRequestId,
  checkoutRepriceRequestCompletedId,
  serviceMode,
  quoteId,
  deliveryAddress,
  serverOrder,
  selectedStoreNumber,
}: {
  checkoutRepriceRequestId: number;
  checkoutRepriceRequestCompletedId: number;
  serviceMode?: ServiceMode | string | null;
  quoteId?: string | null;
  deliveryAddress?: DeliveryAddressForReprice | null;
  serverOrder?: IServerOrder | null;
  selectedStoreNumber?: string | null;
}) => {
  if (!checkoutRepriceRequestId || checkoutRepriceRequestId <= checkoutRepriceRequestCompletedId) {
    return false;
  }

  if (isCheckoutStoreRepriceStale({ selectedStoreNumber, serverOrder })) {
    return false;
  }

  if (
    serviceMode === ServiceMode.DELIVERY &&
    isDeliveryQuoteOrAddressStale({
      quoteId,
      serverOrderDeliveryQuoteId: serverOrder?.delivery?.quoteId,
      deliveryAddress,
      serverOrderDropoff: serverOrder?.delivery?.dropoff,
    })
  ) {
    return false;
  }

  return true;
};

/**
 * Whether a priced server order may settle a specific reprice request for the
 * user's *current* store/delivery selection.
 *
 * Settlement prefers dropoff/store match so a newer quote for the same address
 * can sync local quoteId. A late success for an older address/store must not
 * advance completion for a newer reprice request.
 */
export const shouldSettleCheckoutRepriceFromPricedOrder = ({
  checkoutRepriceRequestId,
  checkoutRepriceRequestCompletedId,
  serviceMode,
  quoteId,
  deliveryAddress,
  serverOrder,
  selectedStoreNumber,
}: {
  checkoutRepriceRequestId: number;
  checkoutRepriceRequestCompletedId: number;
  serviceMode?: ServiceMode | string | null;
  quoteId?: string | null;
  deliveryAddress?: DeliveryAddressForReprice | null;
  serverOrder?: IServerOrder | null;
  selectedStoreNumber?: string | null;
}) => {
  if (!checkoutRepriceRequestId || checkoutRepriceRequestId <= checkoutRepriceRequestCompletedId) {
    return false;
  }

  if (isCheckoutStoreRepriceStale({ selectedStoreNumber, serverOrder })) {
    return false;
  }

  if (serviceMode !== ServiceMode.DELIVERY) {
    return true;
  }

  const serverDropoff = serverOrder?.delivery?.dropoff;
  if (deliveryAddress && serverDropoff) {
    return deliveryAddressesMatch(deliveryAddress, serverDropoff);
  }

  return !isDeliveryQuoteOrAddressStale({
    quoteId,
    serverOrderDeliveryQuoteId: serverOrder?.delivery?.quoteId,
    deliveryAddress,
    serverOrderDropoff: serverDropoff,
  });
};
